/**
 * UTM Parameter Generator
 * Contains functions for generating and determining UTM parameters
 */

/**
 * Sanitize a data-utm-* attribute value before it enters UTM parameters.
 *
 * data-utm-* attributes are admin-set (via template code or the WP admin
 * UI), not end-user input — but they flow into destination URLs, so a
 * stray HTML tag or control character could break the URL or, if the
 * destination renders the parameter, cause an XSS. This strips HTML tags,
 * control characters (except tab/newline which URL-encode safely), and
 * truncates to 200 chars (UTM values are campaign names/URLs, not essays).
 *
 * @param {string} raw The raw attribute value.
 * @return {string} Sanitized value.
 */
function sanitizeUtmValue(raw) {
    if (typeof raw !== 'string') return '';
    return raw
        .replace(/<[^>]*>/g, '')        // strip HTML tags
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // strip control chars (keep \t \n)
        .trim()
        .slice(0, 200);
}

// utm_campaign is truncated to this many characters (determineUtmCampaign()
// below) — keeps a long final path segment (a place/article slug) from
// blowing out GA4/ad-platform campaign-name length limits and report-table
// column widths. Not admin-configurable; a fixed, low-risk cap the plugin
// has always applied.
const CAMPAIGN_MAX_LENGTH = 15;

/**
 * Strip a LEADING `www.` prefix from a hostname. Anchored to the start
 * (`/^www\./`) — pr-review-council finding, v1.6.0: this function used to be
 * an inline `currentHostname.replace('www.', '')` in generateUtmParams()
 * below, which is UNANCHORED (`String.replace()` with a plain string
 * matches the first occurrence ANYWHERE) and would incorrectly strip a
 * mid-string `www.` out of a legitimate hostname shape like
 * `shop.www.example.com`, producing `shop.example.com` instead of leaving
 * it untouched. Exported so `admin/utils/previewUtm.js` can reuse the exact
 * same (correct) logic instead of maintaining an independent copy.
 *
 * @param {string} hostname
 * @return {string}
 */
export function stripWwwPrefix(hostname) {
    return typeof hostname === 'string' ? hostname.replace(/^www\./, '') : hostname;
}

/**
 * Generate UTM parameters for a link
 *
 * @param {HTMLAnchorElement} link The link element
 * @param {number|null} position The link's utm_term position: either the
 *   flat per-link monotonic counter (legacy, positionContainerSelector
 *   unset/empty/invalid/zero-matched), or the 1-based list-item container
 *   rank (src/modules/PositionResolver.js:findItemPosition — v1.10.0).
 *   `null` means "no list-item rank" (an intro link before the first
 *   container) — utm_term is omitted entirely for it, never coerced to a
 *   placeholder value.
 * @param {string} currentHostname Current hostname for utm_source
 * @param {string} currentPathname Current pathname for utm_campaign
 * @param {boolean} mediumEnabled Whether utm_medium is appended at all
 *   (v1.9.0 medium-toggle refactor — REPLACES the page-type classification
 *   subsystem: pathRules collection/native/rating token lists +
 *   per-type mediumValues + domRules DOM heuristics, dead machinery since
 *   every page type resolved to the SAME GA4-safe 'referral' value in 1.7.2).
 * @param {string} mediumValue The single utm_medium value emitted when
 *   mediumEnabled is true.
 * @param {Object} utmContentRules Rules for utm_content determination
 * @param {boolean} normalizeValues Lowercase + collapse whitespace on every
 *   DERIVED value (opt-out feature, F3) — defaults to false so every
 *   caller that omits it is byte-identical.
 *   data-utm-* custom overrides (below) are exempt: they return before this
 *   ever runs, since a hand-set override is the admin's own literal value.
 * @return {Object} UTM parameters
 */
export function generateUtmParams(link, position, currentHostname, currentPathname, mediumEnabled, mediumValue, utmContentRules, normalizeValues = false) {
    const utmParams = {};

    // Check for data-utm-* attributes first (custom overrides)
    const customParams = getCustomUtmParams(link);
    if (Object.keys(customParams).length > 0) {
        return customParams;
    }

    // utm_source - from hostname
    utmParams.utm_source = stripWwwPrefix(currentHostname);

    // utm_medium - the static configured value, omitted entirely when the
    // toggle is off (never an empty string).
    if (mediumEnabled) {
        utmParams.utm_medium = mediumValue;
    }

    // utm_campaign - from current page path
    utmParams.utm_campaign = determineUtmCampaign(currentPathname);

    // utm_content - from link type (button, textlink, banner)
    utmParams.utm_content = determineUtmContent(link, utmContentRules);

    // utm_term - from link position. `null` (v1.10.0, container-rank mode:
    // an intro link before the first list-item container) omits the param
    // entirely — an intro link genuinely has no list-item rank, and
    // omitting is more honest than inventing a 0/blank value. `0` is a
    // real, falsy-but-valid rank (legacy counter starts at 1, but guard on
    // `null` specifically rather than truthiness so a future 0-based
    // caller isn't silently broken).
    if (position !== null) {
        utmParams.utm_term = position + '_position';
    }

    return normalizeValues
        ? Object.fromEntries(Object.entries(utmParams).map(([key, value]) => [key, normalizeUtmValue(value)]))
        : utmParams;
}

/**
 * Normalize a single derived UTM value: lowercase + collapse any run of
 * whitespace to a single underscore. Deliberately does NOT strip hyphens or
 * other URL-safe characters — this closes the "Email vs email vs EMAIL"
 * GA4-fragmentation footgun on values WE derive (hostnames/paths/
 * configured medium labels), nothing more.
 *
 * @param {*} value A derived UTM param value (expected string; anything else
 *   passes through unchanged).
 * @return {*} The normalized string, or the original value if not a string.
 */
export function normalizeUtmValue(value) {
    return typeof value === 'string' ? value.toLowerCase().trim().replace(/\s+/g, '_') : value;
}

/**
 * Get custom UTM parameters from data-utm-* attributes
 * 
 * @param {HTMLAnchorElement} link The link element
 * @return {Object} Custom UTM parameters
 */
export function getCustomUtmParams(link) {
    const customParams = {};

    // Check for data attributes — sanitize each value to strip HTML tags,
    // control characters, and truncate (#49).
    if (link.dataset) {
        if (link.dataset.utmSource) customParams.utm_source = sanitizeUtmValue(link.dataset.utmSource);
        if (link.dataset.utmMedium) customParams.utm_medium = sanitizeUtmValue(link.dataset.utmMedium);
        if (link.dataset.utmCampaign) customParams.utm_campaign = sanitizeUtmValue(link.dataset.utmCampaign);
        if (link.dataset.utmContent) customParams.utm_content = sanitizeUtmValue(link.dataset.utmContent);
        if (link.dataset.utmTerm) customParams.utm_term = sanitizeUtmValue(link.dataset.utmTerm);
    }

    return customParams;
}

/**
 * Determine utm_campaign based on the current page path
 * 
 * @param {string} currentPathname The current page path
 * @return {string} utm_campaign value
 */
export function determineUtmCampaign(currentPathname) {
    const pathSegments = currentPathname.split('/').filter(segment => segment);
    
    // If there are no path segments, use 'homepage'
    if (pathSegments.length === 0) {
        return 'homepage';
    }
    
    // Use the last meaningful segment of the path
    let campaign = pathSegments[pathSegments.length - 1];
    
    // If it's empty, use the previous segment
    if (!campaign && pathSegments.length > 1) {
        campaign = pathSegments[pathSegments.length - 2];
    }
    
    // If still empty, use a default
    if (!campaign) {
        campaign = 'page';
    }
    
    // Cap the value at CAMPAIGN_MAX_LENGTH characters
    if (campaign.length > CAMPAIGN_MAX_LENGTH) {
        campaign = campaign.substring(0, CAMPAIGN_MAX_LENGTH);
    }
    
    return campaign;
}

/**
 * Determine utm_content based on the link type
 * 
 * @param {HTMLAnchorElement} link The link element
 * @param {Object} utmContentRules Rules for determining content type
 * @return {string} utm_content value
 */
export function determineUtmContent(link, utmContentRules) {
    // Default to textlink
    let utmContent = 'textlink';
    
    // Check if it's a button
    if (utmContentRules.button_classes && Array.isArray(utmContentRules.button_classes)) {
        for (let i = 0; i < utmContentRules.button_classes.length; i++) {
            if (link.classList && link.classList.contains(utmContentRules.button_classes[i])) {
                return 'button';
            }
        }
    }
    
    // Check if the link is within an image/banner
    if (utmContentRules.banner_classes && Array.isArray(utmContentRules.banner_classes)) {
        // Check if the link contains an image
        if (link.querySelector && link.querySelector('img')) {
            return 'banner';
        }
        
        // Check if the link is inside a banner element
        let parent = link.parentElement;
        while (parent && parent !== document.body) {
            for (let i = 0; i < utmContentRules.banner_classes.length; i++) {
                if (parent.classList && parent.classList.contains(utmContentRules.banner_classes[i])) {
                    return 'banner';
                }
            }
            parent = parent.parentElement;
        }
    }
    
    return utmContent;
}